/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-11 11:51:42
 *
 *******************************************************/

import _ from 'lodash';
import React, { useState, useCallback } from 'react';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Icon, Popover, Badge } from '@shopify/polaris';

import history from 'src/routes/history';
import FlatIcon from 'src/assets/images/subscription-v4/star.svg';
import { ComposedPropsItem } from './index.type';
import { EXTRA_ACTION } from 'src/helpers/menu-helper';
import { routes } from 'src/routes';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

const MainMenuItem: React.FC<ComposedPropsItem> = ({
  menuId,
  menuIdMetaMatchOldNav,
  position = 'left',
  data,
  i18n,
  className,
  updateFieldReducer
}) => {
  const [popoverActive, setPopoverAction] = useState(false);
  const { id, title, icon, routeTo, children } = data;

  const hasSubMenu = _.isEmpty(routeTo);
  let content = null;

  const togglePopoverActive = useCallback(() => {
    setPopoverAction(prevState => !prevState);
  }, []);

  const location = useLocation();

  const handleExtraAction = (action: string) => {
    switch (action) {
      case EXTRA_ACTION.CLEAR_CACHE_MANAGE_TEMPLATE:
        if (matchPath(routes.shopifyIntegration.manageTemplateInstance(), location.pathname)) {
          return;
        }
        updateFieldReducer('selectedThemeId', '');
        break;
      default:
        break;
    }
  };

  if (hasSubMenu) {
    const activator = (
      <span className={'main-item'}>
        <Icon source={icon} />
        {title}
      </span>
    );

    const actionList = (
      <div className="menu-actions Polaris-ActionList__Actions">
        {children.map(({ title, id, icon, subMenu = [] }) => {
          const onAction = (route: string, extraAction: string) => {
            if (extraAction) {
              handleExtraAction(extraAction);
            }
            if (
              matchPath(routes.shopifyIntegration.manageTemplateInstance(), location.pathname) &&
              route === routes.shopifyIntegration.templateLibrary()
            ) {
              return;
            }

            if (location.pathname !== route) {
              history.push(route);
            }
          };
          return (
            <div id={id} key={id} className="group">
              <div className="group-title">
                <Icon source={icon} />
                {title}
              </div>
              {subMenu
                .filter(sub => !sub.isHide)
                .map(({ title, isNew, id, routeTo, badge, extraAction }) => (
                  <div
                    className={`group-element  Polaris-ActionList__Item ${
                      id === menuId || menuIdMetaMatchOldNav?.indexOf(id) > -1
                        ? 'Polaris-ActionList--active'
                        : ''
                    }`}
                    onClick={() => onAction(routeTo, extraAction)}
                    id={id}
                    key={id}>
                    {title}
                    {badge === 'ai' && (
                      <div className="badge-ai">
                        <img width={12} height={12} src={FlatIcon} alt="wand" />
                      </div>
                    )}
                    {isNew && (
                      <div style={{ marginLeft: '6px' }}>
                        <Badge status="info">
                          <span>{i18n.translate('AdminHTML.MainMenuV2.Common.new')}</span>
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    );

    content = (
      <Popover
        active={popoverActive}
        activator={activator}
        onClose={() => {}}
        preferredAlignment={position}>
        {actionList}
      </Popover>
    );
  } else {
    content = (
      <span
        onClick={() => {
          if (location.pathname !== routeTo) {
            history.push(routeTo);
          }
        }}
        className={'main-item'}>
        <Icon source={icon} />
        {title}
      </span>
    );
  }

  const events = hasSubMenu
    ? {
        onMouseEnter: togglePopoverActive,
        onMouseLeave: togglePopoverActive
      }
    : {};
  return (
    <div
      key={id}
      {...events}
      className={`menuItem ${id} ${menuId.indexOf(id) > -1 ? 'active' : ''} ${className ?? ''} ${
        popoverActive ? 'hovered' : ''
      }`}>
      {content}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    host: _.get(state.oauth, 'host', '')
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(MainMenuItem));
